/* eslint-disable @typescript-eslint/no-empty-function */
import { BusinessStreams } from 'ciam-commons';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useHistory } from 'react-router-dom';
import { DEFAULT_ERROR_MESSAGE } from 'src/constants';
import {
  deleteOrgUser,
  deleteOrgUserBusinessStream,
  deleteOrgUserCreditorNumber,
  getOrgUserDetails,
  resendInvitation,
  resetPassword,
  updateOrgUser,
  updateOrgUserBusinessStream,
  updateOrgUserCreditorNumber,
  updateOrgUserExaminerId,
} from 'src/services/apiService';
import { OrgUser, OrgUserDetails, SystemIDs } from 'src/types';
import { notificationWithTimeoutHandler } from 'src/utils/roleUpdateTimeNotification';
import { capitalizeWord } from 'src/utils/transformers';
import useAuth from './useAuth';
import useNotifications from './useNotifications';

interface OrgUserDetailsState {
  userDetails: OrgUserDetails | null;
  isLoading: number;
  error: string | null;
  showNotification: boolean;
  activateUser: () => void;
  disableUser: () => void;
  fetchUserDetails: () => void;
  resetUserPassword: () => void;
  removeUser: () => void;
  updateUserRoles: (roles: string[]) => void;
  updateUserExaminerId: (systemIDs: SystemIDs[]) => void;
  updateUserCreditorNumber: (creditorNumber: string) => void;
  updateUserBusinessStream: (businessStream: BusinessStreams[]) => void;
  deleteUserCreditorNumber: () => void;
  deleteUserBusinessStream: () => void;
  resendInvitationLink: (userDetails: OrgUser) => void;
}

const OrgUserDetailsContext = createContext<OrgUserDetailsState>({
  userDetails: null,
  isLoading: 0,
  error: null,
  showNotification: false,
  activateUser: () => {
    throw new Error('<OrgUserDetailsProvider> missing in a component tree');
  },
  disableUser: () => {
    throw new Error('<OrgUserDetailsProvider> missing in a component tree');
  },
  fetchUserDetails: () => {
    throw new Error('<OrgUserDetailsProvider> missing in a component tree');
  },
  resetUserPassword: () => {
    throw new Error('<OrgUserDetailsProvider> missing in a component tree');
  },
  removeUser: () => {
    throw new Error('<OrgUserDetailsProvider> missing in a component tree');
  },
  updateUserRoles: () => {
    throw new Error('<OrgUserDetailsProvider> missing in a component tree');
  },
  updateUserExaminerId: () => {
    throw new Error('<OrgUserDetailsProvider> missing in a component tree');
  },
  updateUserCreditorNumber: (): Promise<void> => {
    throw new Error('<OrgUserDetailsProvider> missing in a component tree');
  },
  updateUserBusinessStream: (): Promise<void> => {
    throw new Error('<OrgUserDetailsProvider> missing in a component tree');
  },
  deleteUserCreditorNumber: (): Promise<void> => {
    throw new Error('<OrgUserDetailsProvider> missing in a component tree');
  },
  deleteUserBusinessStream: (): Promise<void> => {
    throw new Error('<OrgUserDetailsProvider> missing in a component tree');
  },
  resendInvitationLink: (): Promise<void> => {
    throw new Error('<OrgUserDetailsProvider> missing in a component tree');
  },
  /*resendInvitationLink: () => {
    return new Promise((_resolve, reject) => {
      try {
        throw new Error('<OrgUserDetailsProvider> missing in a component tree');
      } catch (error) {
        reject(error);
      }
    });
  },*/
});

const useOrgUserDetails = (): OrgUserDetailsState =>
  useContext(OrgUserDetailsContext);

type Props = {
  userId: string;
};

export const OrgUserDetailsProvider: React.FC<Props> = ({
  userId,
  children,
}) => {
  const {
    user: { organisationId },
  } = useAuth();
  const handler = notificationWithTimeoutHandler(userId, organisationId);
  const [userDetails, setUserDetails] = useState<OrgUserDetails>(null);
  const { addNotification } = useNotifications();
  const [isLoading, setIsLoading] = useState<number>(0);
  const [error, setError] = useState(null);
  const [showNotification, setShowNotification] = useState<boolean>(
    !handler.hasTimePassed()
  );

  const history = useHistory();

  const fetchUserDetails = async () => {
    try {
      setIsLoading((prev) => prev + 1);
      setError(null);
      const data = await getOrgUserDetails(organisationId, userId);
      const examinerObj = data?.systemIDs?.find(
        (x) => x.idType === 'examinerId'
      );
      data.examinerId = examinerObj ? examinerObj.idValue : '';
      setUserDetails(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setIsLoading((prev) => prev - 1);
    }
  };

  useEffect(() => {
    fetchUserDetails();

    return () => {
      setIsLoading(0);
      setUserDetails(null);
      setError(null);
    };
  }, [organisationId, userId]);

  const activateUser = useCallback(async () => {
    try {
      setIsLoading((prev) => prev + 1);
      await updateOrgUser(organisationId, userId, {
        status: 'active',
      });
      addNotification({ title: 'User was activated.', timeout: 8000 });
      await fetchUserDetails();
    } catch (e) {
      addNotification({
        title: DEFAULT_ERROR_MESSAGE,
        variant: 'error',
      });
    } finally {
      setIsLoading((prev) => prev - 1);
    }
  }, []);

  const disableUser = useCallback(async () => {
    try {
      setIsLoading((prev) => prev + 1);
      await updateOrgUser(organisationId, userId, {
        status: 'inactive',
      });
      addNotification({ title: 'User was disabled.', timeout: 8000 });
      await fetchUserDetails();
    } catch (e) {
      addNotification({
        title: DEFAULT_ERROR_MESSAGE,
        variant: 'error',
      });
    } finally {
      setIsLoading((prev) => prev - 1);
    }
  }, []);

  const resetUserPassword = useCallback(async () => {
    try {
      setIsLoading((prev) => prev + 1);
      await resetPassword(organisationId, userId);
      addNotification({
        title: 'Password reset email was sent.',
        timeout: 8000,
      });
      await fetchUserDetails();
    } catch (e) {
      addNotification({
        title: DEFAULT_ERROR_MESSAGE,
        variant: 'error',
      });
    } finally {
      setIsLoading((prev) => prev - 1);
    }
  }, []);

  const removeUser = useCallback(async () => {
    const fullName =
      userDetails.firstName && userDetails.lastName
        ? `${capitalizeWord(userDetails.firstName)} ${capitalizeWord(
            userDetails.lastName
          )}`
        : 'User';

    setIsLoading((prev) => prev + 1);
    try {
      await deleteOrgUser(organisationId, userId);
      setUserDetails(null);
      history.goBack();
      addNotification({
        title: `${fullName} was removed from your organisation.`,
        timeout: 8000,
      });
    } catch (e) {
      addNotification({
        title: DEFAULT_ERROR_MESSAGE,
        variant: 'error',
      });
    } finally {
      setIsLoading((prev) => prev - 1);
    }
  }, [userDetails]);

  const handleNotification = () => {
    handler.saveTimestamp();
    handler.startTimeout(setShowNotification);
  };

  const updateUserRoles = useCallback(
    async (roles: string[]) => {
      try {
        setIsLoading((prev) => prev + 1);
        await updateOrgUser(organisationId, userId, {
          status: 'active',
          roles,
        });
        addNotification({ title: 'User roles were updated.', timeout: 8000 });

        handleNotification();
        await fetchUserDetails();
      } catch (e) {
        addNotification({
          title: DEFAULT_ERROR_MESSAGE,
          variant: 'error',
        });
      } finally {
        setIsLoading((prev) => prev - 1);
      }
    },
    [userDetails]
  );

  const updateUserExaminerId = useCallback(async (systemIDs: SystemIDs[]) => {
    try {
      setIsLoading((prev) => prev + 1);
      await updateOrgUserExaminerId(organisationId, userId, systemIDs);
      addNotification({
        title: 'Examiner ID was updated.',
        timeout: 8000,
      });
      await fetchUserDetails();
    } catch (e) {
      addNotification({
        title: `Failed to update examiner ID.`,
        variant: 'error',
      });
    } finally {
      setIsLoading((prev) => prev - 1);
    }
  }, []);

  const updateUserCreditorNumber = useCallback(
    async (creditorNumber: string) => {
      try {
        setIsLoading((prev) => prev + 1);
        await updateOrgUserCreditorNumber(
          organisationId,
          userId,
          creditorNumber
        );
        addNotification({
          title: 'Creditor number was updated.',
          timeout: 8000,
        });
        await fetchUserDetails();
      } catch (e) {
        addNotification({
          title: `Failed to update creditor number.`,
          variant: 'error',
        });
      } finally {
        setIsLoading((prev) => prev - 1);
      }
    },
    []
  );

  const updateUserBusinessStream = useCallback(
    async (businessStream: BusinessStreams[]) => {
      try {
        setIsLoading((prev) => prev + 1);
        await updateOrgUserBusinessStream(
          organisationId,
          userId,
          businessStream
        );
        addNotification({
          title: 'Business unit was updated.',
          timeout: 8000,
        });
        await fetchUserDetails();
      } catch (e) {
        addNotification({
          title: `Failed to update business unit.`,
          variant: 'error',
        });
      } finally {
        setIsLoading((prev) => prev - 1);
      }
    },
    []
  );

  const deleteUserCreditorNumber = useCallback(async () => {
    try {
      setIsLoading((prev) => prev + 1);
      await deleteOrgUserCreditorNumber(organisationId, userId);
      addNotification({
        title: 'Creditor number was updated.',
        timeout: 8000,
      });
      await fetchUserDetails();
    } catch (e) {
      addNotification({
        title: `Failed to update creditor number.`,
        variant: 'error',
      });
    } finally {
      setIsLoading((prev) => prev - 1);
    }
  }, []);

  const deleteUserBusinessStream = useCallback(async () => {
    try {
      setIsLoading((prev) => prev + 1);
      await deleteOrgUserBusinessStream(organisationId, userId);
      addNotification({
        title: 'Business unit was updated.',
        timeout: 8000,
      });
      await fetchUserDetails();
    } catch (e) {
      addNotification({
        title: `Failed to update business unit.`,
        variant: 'error',
      });
    } finally {
      setIsLoading((prev) => prev - 1);
    }
  }, []);

  const resendInvitationLink = useCallback(async (userInfo: OrgUser) => {
    try {
      setIsLoading((prev) => prev + 1);
      await resendInvitation(userInfo);
      addNotification({
        title: 'Invitation email was sent.',
        timeout: 8000,
      });
    } catch (e) {
      addNotification({
        title: DEFAULT_ERROR_MESSAGE,
        variant: 'error',
      });
    } finally {
      setIsLoading((prev) => prev - 1);
    }
  }, []);

  return (
    <OrgUserDetailsContext.Provider
      value={{
        userDetails,
        isLoading,
        error,
        showNotification,
        fetchUserDetails,
        activateUser,
        disableUser,
        resetUserPassword,
        removeUser,
        updateUserRoles,
        updateUserExaminerId,
        updateUserCreditorNumber,
        updateUserBusinessStream,
        deleteUserCreditorNumber,
        deleteUserBusinessStream,
        resendInvitationLink,
      }}
    >
      {children}
    </OrgUserDetailsContext.Provider>
  );
};

export default useOrgUserDetails;
